import type { RovoLabsConfirmedTools } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type {
	MessageAction,
	MessageActionPayload,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';

/**
 * Shape forwarded as the `tools` analytics attribute on tools-execution
 * related events (`aiResult initiated/viewed/actioned/error` and
 * `rovoChatMessageAction success/error/actioned`). Mirrors the tools attribute
 * spec at:
 * https://hello.atlassian.net/wiki/spaces/~598965687/pages/6881328891/Tools+attributes+analytics+spec
 *
 * Treated as the same class of attribute as `pluginsInvoked` — it is forwarded
 * through `trackAIMAUEvent` via the existing `[key: string]: any` index
 * signature on `TrackAIEventPayload`, while the shape is constrained at the
 * call site by this type.
 */
export type ToolAnalyticsAttribute = {
	toolId: string;
	/**
	 * Raw tool key (e.g. `confluence-page-create`).
	 */
	toolKey?: string;
	/**
	 * Raw tool source string from the backend (e.g. `'MCP_TOOL'`, `'CONVO_AI'`)
	 * — surfaces where the tool definition originated. Named
	 * `toolDefinitionSource` in the emitted analytics payload per the RFC.
	 */
	toolDefinitionSource: string | null;
	/**
	 * Whether the tool integration is owned by Atlassian or a third party.
	 * TODO add when BE is returning the data
	 */
	// toolIntegrationOwner: string;
	/**
	 * Whether invoking this tool is considered consequential (i.e. carries a
	 * meaningful side-effect such as creating, mutating, or deleting data).
	 * TODO add when BE is returning the data
	 */
	// isConsequential: boolean;
};

/**
 * Variant emitted on user-driven `actioned` events — adds the resolution that
 * triggered the event (confirmed vs cancelled vs externally actioned).
 */
export type ToolActionedAnalyticsAttribute = ToolAnalyticsAttribute & {
	resolutionType: string;
};

const UNKNOWN_TOOL_ID = 'UNKNOWN_TOOL_ID';
const MCP_TOOL_SOURCE = 'MCP_TOOL';

/**
 * MCP tool keys are user-generated content and must not leak into analytics
 * payloads. For all other sources (including a missing `toolSource`) we
 * forward the raw key.
 */
const redactToolKeyForPii = ({
	toolDefinitionSource,
	key,
}: {
	toolDefinitionSource: string | undefined;
	key: string | undefined;
}): string | undefined => (toolDefinitionSource === MCP_TOOL_SOURCE ? undefined : key);

type ToolAttributeInput = {
	toolId: string | undefined;
	toolKey: string | undefined;
	toolDefinitionSource: string | undefined;
};

const buildBaseToolAttribute = ({
	toolId,
	toolKey,
	toolDefinitionSource,
}: ToolAttributeInput): ToolAnalyticsAttribute => ({
	toolId: toolId ?? UNKNOWN_TOOL_ID,
	toolKey: redactToolKeyForPii({ toolDefinitionSource, key: toolKey }),
	toolDefinitionSource: toolDefinitionSource ?? null,
});

/**
 * Builds the `tools` attribute array from a `RovoLabsConfirmedTools` payload —
 * used in the resumption flow
 * `sendMessageConfirmedTools` with the tools the user just confirmed/cancelled.
 */
export const buildToolsAttributeFromConfirmedTools = (
	confirmedTools: RovoLabsConfirmedTools,
): ToolActionedAnalyticsAttribute[] =>
	confirmedTools.tools.map((tool) => ({
		...buildBaseToolAttribute({
			toolId: tool.tool_id,
			toolKey: tool.key,
			toolDefinitionSource: tool.tool_source,
		}),
		resolutionType: tool.resolution?.type ?? 'UNKNOWN_RESOLUTION_TYPE',
	}));

/**
 * Builds the `tools` attribute array from a single `MessageActionPayload`
 * context — used in the Custom UI flow where each `rovoChatMessageAction`
 * resolves a single tool action. Per the FINAL_RESPONSE shape the action
 * `actionId` maps to `toolId`, `key` to `toolKey`, and `toolSource` carries
 * the raw provider (e.g. `'MCP_TOOL'`).
 */
export const buildToolsAttributeFromActionContext = (
	context: MessageActionPayload['context'],
): ToolActionedAnalyticsAttribute[] => [
	{
		...buildBaseToolAttribute({
			toolId: context.actionId,
			toolKey: context.key,
			toolDefinitionSource: context.toolSource,
		}),
		resolutionType: context.resolution?.type ?? 'UNKNOWN_RESOLUTION_TYPE',
	},
];

/**
 * Builds the `tools` attribute array from the `actions` field on an assistant
 * message — used by the post-stream `aiResult viewed` event where the tools
 * being announced are the ones the agent is proposing to invoke (no
 * resolution yet).
 */
export const buildToolsAttributeFromMessageActions = (
	actions: MessageAction[],
): ToolAnalyticsAttribute[] =>
	actions.map((action) =>
		buildBaseToolAttribute({
			toolId: action.actionId,
			toolKey: action.key,
			toolDefinitionSource: action.toolSource,
		}),
	);
